<template>
<div class="flex flex-col form-input" :class="class" :data-test="`${dataTest}__wrapper`">
	<div class="form-input-phone">
		<div class="bg-white phone-input-wrapper" :class="[Disabled ? 'text-neutral !bg-neutral-50 !border-neutral-50' : '', wrapperOutlineClass]">
			<DropdownSelect v-model="ModelValueObject.prefix" :Items="PhoneCountryPrefixesList" :Disabled="Disabled" :data-test="`${dataTest}__dropdown`" @change="PrefixChange"/>
			<div class="w-px h-full mr-3 border-r separator border-neutral-200" :class="[ Disabled ? 'opacity-0' : '', wrapperOutlineClass]"/>
			<input
				:id="`${name}__input`"
				ref="phoneInput"
				v-model="phoneNumber"
				type="tel"
				:placeholder="Placeholder"
				:autocomplete="Autocomplete"
				:data-test="`${dataTest}__input`"
				:disabled="Disabled"
				:maxlength="ModelValueObject.prefix === '+48' ? 11 : 15"
				:data-invalid="Boolean(validationMessage)"
				@keyup="onKeyUp"
				@keydown="onKeyDown"
				@input="FormatNumber"
				@change="onChange"
			>
		</div>
	</div>
	<label v-if="Label !== ''" :for="`${name}__input`" class="form-input-label" v-html="Label"/>
	<div v-if="!HideValidation" class="form-input-validation | text-xs mt-1" :class="Disabled ? 'text-neutral-600' : 'text-danger'" v-html="validationMessage"/>
</div>
</template>

<script lang="ts">
import { PhoneValue, CountryEntry } from '@globals/types/components/PhoneInput';
import { PhoneCountryPrefixes } from '@globals/utilities/PhoneCountryPrefixes';

export default {
	name: 'PhoneInput',
	props: {
		modelValue: {
			type: String,
			required: true,
		},
		name: {
			type: String,
			default: 'nipInput',
		},
		dataTest: {
			type: String,
			default: 'nip',
		},
		doNotBlock: {
			type: Boolean,
			default: false,
		},
		Disabled: {
			type: Boolean,
			default: false,
		},
		Label: {
			type: String,
			default: 'Telefon',
		},
		Placeholder: {
			type: String,
			default: 'Wpisz numer telefonu',
		},
		Autocomplete: {
			type: String,
			default: 'off',
		},
		HideValidation: {
			type: Boolean,
			default: false,
		},
		Focus: {
			type: Boolean,
			default: false,
		},
		class: {
			type: String,
			default: '',
		},
	},
	emits: [
		'update:modelValue', 'invalid',
	],
	data() {
		return {
			phoneNumber: '',
			lastInputLength: 0,
			validationMessage: '',
			isDisabled: false,
			prefix: '+48',
		};
	},
	computed: {
		wrapperOutlineClass() {
			if (!this.validationMessage) {
				return '';
			}

			if (this.Disabled) {
				return '';
			}

			if (this.validationMessage || this.ModelValueObject.value.length) {
				return '!border-danger';
			}

			return '';
		},
		PhoneCountryPrefixesList() {
			const result = [] as CountryEntry[];
			PhoneCountryPrefixes.forEach((p) => {
				result.push({ Value: p.dial_code, Label: `<span class="text-neutral-900">${p.dial_code}</span>` });
			});
			if (this.ModelValueObject.prefix !== '+48') {
				const prefix = PhoneCountryPrefixes.find((p) => p.dial_code === this.ModelValueObject.prefix);
				if (prefix) {
					const index = result.findIndex((p) => p.Value === prefix.dial_code);
					if (index > -1) {
						result.splice(index, 1);
					}
					result.unshift({ Value: prefix.dial_code, Label: `<span class="text-neutral-900">${prefix.dial_code}</span>` });
				}
			}

			return result;
		},
		ModelValueObject(): PhoneValue {
			if (!this.modelValue) { return { prefix: '+48', value: '' }; }
			for (const prefix of PhoneCountryPrefixes) {
				let phone = this.modelValue;
				if (phone.startsWith('00') || phone.startsWith('+00')) {
					phone = phone.replace(/^(\+?00)/, '+');
				}
				if (phone.startsWith(prefix.dial_code)) {
					return { prefix: prefix.dial_code, value: phone.replace(prefix.dial_code, '') };
				}
			}
			return { prefix: '+48', value: this.modelValue };
		},
	},
	mounted() {
		this.phoneNumber = this.ModelValueObject.value;
		this.FormatNumber();
		if (this.Focus) {
			setTimeout(() => {
				(this.$refs.phoneInput as HTMLInputElement).focus();
			}, 100);
		}
	},
	methods: {
		onKeyUp() {
			this.$emit('update:modelValue', (this.phoneNumber !== '' ? this.prefix : '') + this.phoneNumber.replace(/\s/g, ''));
		},
		onKeyDown(e:KeyboardEvent) {
			if (e.ctrlKey) { return; }

			if (e.key === 'Enter') {
				(this.$refs.phoneInput as HTMLInputElement).blur();
			}

			const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab', 'Home', 'End'];
			if (!allowedKeys.includes(e.key)) {
				e.preventDefault();
			}
		},
		onChange() {
			this.ValidatePhoneNumber();
		},
		onInput() {
			this.$emit('update:modelValue', (this.phoneNumber !== '' ? this.ModelValueObject.prefix : '') + this.phoneNumber.replace(/\s/g, ''));
		},
		FormatNumber() {
			let cursorPosition = (this.$refs.phoneInput as HTMLInputElement).selectionStart || 0;
			let numbers = this.phoneNumber.replace(/\D/g, '');
			const char: { [key: number]: string } = this.ModelValueObject.prefix === '+48' ? { 2: ' ', 5: ' ' } : {};
			numbers = this.ModelValueObject.prefix === '+48' ? numbers.slice(0, 9) : numbers.slice(0, 15);
			this.phoneNumber = '';
			for (let i = 0; i < numbers.length; i++) {
				this.phoneNumber += numbers[i] + (char[i] || '');
			}
			if (this.phoneNumber.length < this.lastInputLength) {
				if (this.phoneNumber[cursorPosition - 1] === ' ') {
					cursorPosition -= 1;
				}
			} else if (this.phoneNumber[cursorPosition - 1] === ' ') {
				cursorPosition += 1;
			}
			this.lastInputLength = this.phoneNumber.length;
			this.$nextTick(() => {
				(this.$refs.phoneInput as HTMLInputElement).selectionStart = cursorPosition;
				(this.$refs.phoneInput as HTMLInputElement).selectionEnd = cursorPosition;
			});
		},
		ValidatePhoneNumber() {
			this.validationMessage = '';

			const value = this.phoneNumber.replace(/\D/g, '');

			if (this.ModelValueObject.prefix === '+48' && value.length > 0 && value.length < 9) {
				this.validationMessage = 'Wpisz właściwy numer telefonu';
			} else {
				this.validationMessage = '';
				if (this.ModelValueObject.prefix === '+48' && value.length > 9) {
					this.validationMessage = 'Wpisz właściwy numer telefonu';
				}
			}

			this.$emit('invalid', this.validationMessage);
			return this.validationMessage;
		},
		PrefixChange(newPrefix: string) {
			this.prefix = newPrefix;
			this.$emit('update:modelValue', newPrefix + this.phoneNumber.replace(/\s/g, ''));
			this.FormatNumber();
			this.onInput();
			this.ValidatePhoneNumber();
		},
	},
};
</script>

<style lang="scss">

.form-input-phone {
	> div:first-child {
		display: flex;
		align-items: center;
		margin-top: 2px;
		margin-bottom: 2px;
		white-space: nowrap;
		min-height: 3rem;
		height: 3rem;
		border-radius: 0.5rem !important;
		border: 1px solid var(--neutral-200);

		@media (min-width: 768px) {
			min-height: 3.5rem;
			height: 3.5rem;
		}

		> *:first-child {
			border-bottom-left-radius: 0.4rem !important;
			border-top-left-radius: 0.4rem !important;
		}

		> *:last-child {
			border-bottom-right-radius: 0.4rem;
			border-top-right-radius: 0.4rem;
		}

		input {
			border: none !important;
			min-height: 40px;
			max-height: 40px;
			padding-right: 0.25rem;
			padding-top: 16px;
			padding-bottom: 16px;
			margin-top: 0;
			margin-bottom: 0;

			@media (min-width: 768px) {
				max-height: 48px;
			}
		}

		button {
			height: 40px;

			@media (min-width: 768px) {
				height: 48px;
			}
		}
	}

	.dropdown {
		padding-left: 2px;
	}

	&:focus-within {
		> div:first-child {
			border: 1px solid var(--neutral-400);

			.separator {
				border-right: 1px solid var(--neutral-400);
			}
		}
	}

	.dropdown-button:disabled {
		color: var(--neutral-500);
		background: var(--neutral-50) !important;
		opacity: 1 !important;
	}

	.dropdown:not(.is-arrowless)>.dropdown-button:after {
		width: 12px;
		height: 12px;
		margin: 4px;
		background: currentColor;
		clip-path: polygon(50% 75%,15% 30%,85% 30%);
		content: ""
	}
}
</style>
